import { GetHospitalListApiType } from 'models/DCKAdmin/AdminManageHospitalPageTypes';
import { Body3Medium } from 'styles/Global/typography';
import styled from 'styled-components';
import { BorderColor } from 'styles/Global/colorSemantic';
import { RowDivider } from 'styles/Global/commonStyles';
import { PositiveButton, NegativeButton } from 'components/Buttons/VariantsButtons';
import { useState, useRef, useEffect } from 'react';
import { useLoginStore } from 'store/useLoginDataStore';
import { useLoadingStore } from 'store/useLoadingStore';
import axios from 'axios';
import { apiPaths } from 'constants/path';
import { tokenHeader } from 'functions/apiFunctions';
import BasicConfirmModal from 'components/Modal/BasicConfirmModal';

interface OperatingHour {
    day: number;
    open: string;
    close: string;
    isHoliday: boolean;
    breakTime: string | undefined;
}

interface Product {
    _id: string;
    externalCode: string;
    productName: string;
    retailPrice: number;
    providerName: string;
}

interface AdminManageHospitalDetailProps {
    hospital?: GetHospitalListApiType;
    onBack: () => void;
    onUpdate: () => void;
    isNew?: boolean;
}

const initialHospitalState: GetHospitalListApiType = {
    _id: '',
    name: undefined,
    tel: undefined,
    code: undefined,
    address: undefined,
    addressDetail: undefined,
    zipCode: undefined,
    phoneNumber: undefined,
    isShow: true,
    doctors: [],
    userCount: undefined,
    doctorCount: undefined,
    diagnosisCount: undefined,
    patientCount: undefined,
    completedAssessmentCount: undefined,
    totalAmount: undefined,
    bio: undefined,
    departments: undefined,
    images: undefined,
    lat: undefined,
    lng: undefined,
    timestamp: undefined,
    services: [],
    mallItemCodes: [],
    operatingHours: [
        { day: 0, open: '09:00', close: '18:00', isHoliday: false, breakTime: '13:00~14:00' },
        { day: 1, open: '09:00', close: '18:00', isHoliday: false, breakTime: '13:00~14:00' },
        { day: 2, open: '09:00', close: '18:00', isHoliday: false, breakTime: '13:00~14:00' },
        { day: 3, open: '09:00', close: '18:00', isHoliday: false, breakTime: '13:00~14:00' },
        { day: 4, open: '09:00', close: '18:00', isHoliday: false, breakTime: '13:00~14:00' },
        { day: 5, open: '09:00', close: '18:00', isHoliday: false, breakTime: '13:00~14:00' },
        { day: 6, open: '09:00', close: '18:00', isHoliday: false, breakTime: '13:00~14:00' },
        { day: 7, open: '09:00', close: '18:00', isHoliday: true, breakTime: '13:00~14:00' }
    ]
};

const initialOperatingHours: OperatingHour[] = [
    { day: 0, open: '09:00', close: '18:00', isHoliday: false, breakTime: '13:00~14:00' },
    { day: 1, open: '09:00', close: '18:00', isHoliday: false, breakTime: '13:00~14:00' },
    { day: 2, open: '09:00', close: '18:00', isHoliday: false, breakTime: '13:00~14:00' },
    { day: 3, open: '09:00', close: '18:00', isHoliday: false, breakTime: '13:00~14:00' },
    { day: 4, open: '09:00', close: '18:00', isHoliday: false, breakTime: '13:00~14:00' },
    { day: 5, open: '09:00', close: '18:00', isHoliday: false, breakTime: '13:00~14:00' },
    { day: 6, open: '09:00', close: '18:00', isHoliday: false, breakTime: '13:00~14:00' },
    { day: 7, open: '09:00', close: '18:00', isHoliday: true, breakTime: '13:00~14:00' }
];

const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
};

const AdminManageHospitalDetail = ({ hospital, onBack, onUpdate, isNew = false }: AdminManageHospitalDetailProps) => {
    const [isEditing, setIsEditing] = useState(isNew);
    const [editedHospital, setEditedHospital] = useState<GetHospitalListApiType>(() => {
        if (!hospital) {
            return {
                ...initialHospitalState,
                operatingHours: initialOperatingHours
            };
        }
        return {
            ...hospital,
            departments: hospital.departments || [],
            bio: hospital.bio || '',
            phoneNumber: hospital.phoneNumber || '',
            lat: hospital.lat || 0,
            lng: hospital.lng || 0,
            doctors: hospital.doctors || [],
            images: hospital.images || [],
            services: hospital.services || [],
            operatingHours: hospital.operatingHours || initialOperatingHours
        } as GetHospitalListApiType;
    });
    const [previewImages, setPreviewImages] = useState<{ file?: File; url: string }[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { loginData } = useLoginStore();
    const { setLoading } = useLoadingStore();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
    const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [selectedPreviewImage, setSelectedPreviewImage] = useState<string>('');
    const [doctorImageFile, setDoctorImageFile] = useState<File | null>(null);
    const [selectedDoctorIndex, setSelectedDoctorIndex] = useState<number | null>(null);
    const doctorFileInputRef = useRef<HTMLInputElement>(null);
    const [isDeleteHospitalModalOpen, setIsDeleteHospitalModalOpen] = useState(false);
    const [products, setProducts] = useState<Product[]>([]);
    const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isProductListVisible, setIsProductListVisible] = useState(false);

    useEffect(() => {
        if (hospital?.images) {
            setPreviewImages(hospital.images.map(url => ({ url })));
        }
    }, [hospital]);

    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isPreviewModalOpen) {
                setIsPreviewModalOpen(false);
            }
        };

        if (isPreviewModalOpen) {
            window.addEventListener('keydown', handleEscKey);
        }

        return () => {
            window.removeEventListener('keydown', handleEscKey);
        };
    }, [isPreviewModalOpen]);

    useEffect(() => {
        if (hospital?.mallItemCodes) {
            setSelectedProducts(hospital.mallItemCodes);
        }
    }, [hospital]);

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                apiPaths.getProducts,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `${process.env.REACT_APP_AUTH_KEY}`,
                    }
                }
            );

            if (response.data.status === 'ok') {
                console.log(response.data.data);
                setProducts(response.data.data);
            }
        } catch (error) {
            console.error('상품 목록 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancel = () => {
        if (hospital) {
                    setEditedHospital({
            ...hospital,
            departments: hospital.departments || [],
            bio: hospital.bio || '',
            phoneNumber: hospital.phoneNumber || '',
            lat: hospital.lat || 0,
            lng: hospital.lng || 0,
            doctors: hospital.doctors || [],
            images: hospital.images || [],
            services: hospital.services || [],
            operatingHours: hospital.operatingHours || initialOperatingHours
        } as GetHospitalListApiType);
        }
        setIsEditing(false);
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;

        const newImages = Array.from(files).map(file => ({
            file,
            url: URL.createObjectURL(file)
        }));
        setPreviewImages(prev => [...prev, ...newImages]);
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const formData = new FormData();
            
            // 이미지 파일 추가
            previewImages.forEach((image) => {
                if (image.file) {
                    formData.append('images', image.file);
                }
            });

            // 기존 병원 데이터 추가
            Object.keys(editedHospital).forEach((key) => {
                if (key !== 'images' && (!isNew || (key !== '_id' && key !== 'timestamp'))) {
                    const value = editedHospital[key as keyof GetHospitalListApiType];
                    
                    // 값이 있는 경우에만 추가
                    if (value !== undefined && value !== null && value !== '') {
                        if (key === 'doctors') {
                            // doctors 배열의 각 요소를 개별적으로 추가
                            const doctors = value as any[];
                            doctors.forEach((doctor, index) => {
                                Object.keys(doctor).forEach(doctorKey => {
                                    const doctorValue = doctor[doctorKey];
                                    // 의사 정보에서도 빈 값은 제외
                                    if (doctorValue !== undefined && doctorValue !== null && doctorValue !== '') {
                                        if (doctorKey === 'departments') {
                                            // departments 배열의 각 요소를 개별적으로 추가
                                            (doctorValue as string[]).forEach((dept, deptIndex) => {
                                                if (dept) {
                                                    formData.append(`doctors[${index}][${doctorKey}][${deptIndex}]`, dept);
                                                }
                                            });
                                        } else if (doctorKey === 'level' || doctorKey === 'status') {
                                            formData.append(`doctors[${index}][${doctorKey}]`, String(Number(doctorValue)));
                                        } else {
                                            formData.append(`doctors[${index}][${doctorKey}]`, doctorValue);
                                        }
                                    }
                                });
                            });
                        } else if (key === 'operatingHours') {
                            // operatingHours를 JSON 문자열로 변환하여 추가
                            formData.append(key, JSON.stringify(value));
                        } else if (key === 'services') {
                            // services 배열의 각 요소를 개별적으로 추가
                            const services = value as string[];
                            services.forEach((service, index) => {
                                formData.append(`services[${index}]`, service);
                            })
                        } else if (key === 'mallItemCodes') {
                            // mallItemCodes 배열의 각 요소를 개별적으로 추가
                            (value as string[]).forEach((code, index) => {
                                formData.append(`mallItemCodes[${index}]`, code);
                            });
                        } else {
                            formData.append(key, value as string);
                        }
                    }
                }
            });

            const response = await axios[isNew ? 'post' : 'put'](
                isNew ? apiPaths.createHospital : apiPaths.updateHospital,
                formData,
                {
                    headers: {
                        ...tokenHeader(loginData.token),
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.status === 'ok') {
                console.log(`response.data.data:: !@#$$ `, response.data.data);
                onUpdate();
                setIsEditing(false);
            }
        } catch (error) {
            console.error('병원 정보 저장 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddDoctor = () => {
        setEditedHospital(prev => ({
            ...prev,
            doctors: [
                ...(prev.doctors || []),
                {
                    name: '',
                    image: '',
                    departments: []
                }
            ]
        }));
    };

    const handleRemoveDoctor = (index: number) => {
        setEditedHospital(prev => ({
            ...prev,
            doctors: (prev.doctors || []).filter((_, i) => i !== index)
        }));
    };

    const handleDoctorChange = (index: number, field: string, value: string | string[]) => {
        setEditedHospital(prev => ({
            ...prev,
            doctors: (prev.doctors || []).map((doctor, i) => 
                i === index ? { 
                    ...doctor, 
                    [field]: field === 'departments' 
                        ? (typeof value === 'string' ? value.split(',').map(item => item.trim()) : value)
                        : field === 'level' || field === 'status'
                            ? Number(value)
                            : value 
                } : doctor
            )
        }));
    };

    const handleChange = (field: keyof GetHospitalListApiType, value: string | number | string[] | boolean) => {
        setEditedHospital(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleDeleteImageClick = (index: number) => {
        setSelectedImageIndex(index);
        setIsDeleteModalOpen(true);
    };

    const handleRemoveImage = async () => {
        if (selectedImageIndex === null) return;
        
        try {
            setLoading(true);
            const image = previewImages[selectedImageIndex];
            
            if (!image.file) {
                // URL에서 마지막 '/' 이후의 파일명만 추출
                const fileName = image.url.split('/').pop();
                if (!fileName) {
                    throw new Error('이미지 파일명을 찾을 수 없습니다');
                }
                
                const response = await axios.delete(
                    `${apiPaths.deleteHospitalImage}/${editedHospital._id}/${fileName}`,
                    {
                        headers: tokenHeader(loginData.token)
                    }
                );

                if (response.data.status === 'ok') {
                    setPreviewImages(prev => {
                        const newImages = [...prev];
                        newImages.splice(selectedImageIndex, 1);
                        return newImages;
                    });
                }
            } else {
                // 새로 추가된 이미지인 경우 로컬에서만 제거
                setPreviewImages(prev => {
                    const newImages = [...prev];
                    URL.revokeObjectURL(newImages[selectedImageIndex].url);
                    newImages.splice(selectedImageIndex, 1);
                    return newImages;
                });
            }
        } catch (error) {
            console.error('이미지 삭제 실패:', error);
        } finally {
            setLoading(false);
            setIsDeleteModalOpen(false);
            setSelectedImageIndex(null);
        }
    };

    const handleSaveClick = () => {
        setIsSaveModalOpen(true);
    };

    const handleSaveConfirm = async () => {
        setIsSaveModalOpen(false);
        await handleSave();
    };

    const handleImagePreview = (imageUrl: string) => {
        if (!isEditing) {
            setSelectedPreviewImage(imageUrl);
            setIsPreviewModalOpen(true);
        }
    };

    const handleDoctorImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const doctor = editedHospital.doctors[index];
        if (!doctor || !editedHospital._id || !doctor.name) {
            console.error('의사 정보가 없습니다.');
            return;
        }

        setDoctorImageFile(file);
        setSelectedDoctorIndex(index);

        try {
            setLoading(true);
            const formData = new FormData();
            formData.append('id', editedHospital._id);
            formData.append('name', doctor.name);
            formData.append('image', file, file.name);

            const response = await axios.put(
                apiPaths.updateDoctor,
                formData,
                {
                    headers: {
                        ...tokenHeader(loginData.token),
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.status === 'ok') {
                const updatedDoctor = response.data.data.doctors.find((d: any) => d.name === doctor.name);
                if (updatedDoctor) {
                    setEditedHospital(prev => ({
                        ...prev,
                        doctors: prev.doctors.map((doc, i) => 
                            i === index ? { ...doc, image: updatedDoctor.image } : doc
                        )
                    }));
                }
            } else {
                console.error('의사 사진 업로드 실패:', response.data.message);
            }
        } catch (error) {
            console.error('의사 사진 업로드 실패:', error);
        } finally {
            setLoading(false);
            setDoctorImageFile(null);
            setSelectedDoctorIndex(null);
        }
    };

    const handleDeleteHospital = async () => {
        if (!hospital?._id) return;
        
        setLoading(true);
        try {
            const response = await axios.delete(
                apiPaths.deleteHospital + hospital._id,
                {
                    headers: tokenHeader(loginData.token)
                }
            );

            if (response.data.status === 'ok') {
                onUpdate();
                onBack();
            }
        } catch (error) {
            console.error('병원 삭제 실패:', error);
        } finally {
            setLoading(false);
            setIsDeleteHospitalModalOpen(false);
        }
    };

    const handleOperatingHourChange = (index: number, field: keyof OperatingHour, value: string | boolean | undefined) => {
        setEditedHospital(prev => ({
            ...prev,
            operatingHours: (prev.operatingHours || initialOperatingHours).map((hour, i) => 
                i === index ? { 
                    ...hour, 
                    [field]: field === 'open' || field === 'close' ? formatTime(value as string) : value 
                } : hour
            )
        }));
    };

    const handleServiceChange = (serviceCode: string, checked: boolean) => {
        setEditedHospital(prev => ({
            ...prev,
            services: checked 
                ? [...(prev.services || []), serviceCode]
                : (prev.services || []).filter(service => service !== serviceCode)
        }));
    };

    const handleProductClick = (externalCode: string) => {
        if (!isEditing) return;
        
        const isSelected = selectedProducts.includes(externalCode);
        let newSelectedProducts: string[];
        
        if (isSelected) {
            newSelectedProducts = selectedProducts.filter(code => code !== externalCode);
        } else {
            newSelectedProducts = [...selectedProducts, externalCode];
        }
        
        setSelectedProducts(newSelectedProducts);
        handleChange('mallItemCodes', newSelectedProducts);
    };

    const handleRemoveProduct = (externalCode: string) => {
        const newSelectedProducts = selectedProducts.filter(code => code !== externalCode);
        setSelectedProducts(newSelectedProducts);
        handleChange('mallItemCodes', newSelectedProducts);
    };

    const getSelectedProductInfo = (externalCode: string) => {
        return products.find(product => product.externalCode === externalCode);
    };

    const filteredProducts = products.filter(product => 
        product.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.providerName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <>
            <DetailSection>
                <Header>
                    <Title>{!hospital ? '병원 등록' : '병원 상세 정보'}</Title>
                    <ButtonGroup>
                        {isEditing && !isNew ? (
                            <>
                                <PositiveButton onClick={handleSaveClick}>저장</PositiveButton>
                                <NegativeButton onClick={handleCancel}>취소</NegativeButton>
                                <NegativeButton onClick={onBack}>나가기</NegativeButton>
                            </>
                        ) : isNew ? (
                            <>
                                <PositiveButton onClick={handleSaveClick}>저장</PositiveButton>
                                <NegativeButton onClick={onBack}>나가기</NegativeButton>
                            </>
                        ) : (
                            <>
                                <PositiveButton onClick={handleEdit}>수정</PositiveButton>
                                <NegativeButton onClick={onBack}>나가기</NegativeButton>
                            </>
                        )}
                        {!isNew && !isEditing && (
                            <DeleteButton onClick={() => setIsDeleteHospitalModalOpen(true)}>
                                삭제
                            </DeleteButton>
                        )}
                    </ButtonGroup>
                </Header>
                <RowDivider $backgroundcolor={BorderColor.color_border_neutral} $marginbottom='16px' />
                <Content>
                    <InfoRow>
                        <Label>병원명</Label>
                        <Input
                            value={editedHospital.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            placeholder="병원명을 입력하세요"
                            disabled={!isEditing}
                            style={{ flex: 1 }}
                        />
                        <Label style={{ marginLeft: '10px' }}>전화번호</Label>
                        <Input
                            value={editedHospital.tel}
                            onChange={(e) => handleChange('tel', e.target.value)}
                            placeholder="전화번호를 입력하세요"
                            disabled={!isEditing}
                            style={{ flex: 1 }}
                        />
                        <Label style={{ marginLeft: '10px' }}>노출여부</Label>
                        <Select
                            value={editedHospital.isShow ? 'true' : 'false'}
                            onChange={(e) => handleChange('isShow', e.target.value === 'true')}
                            disabled={!isEditing}
                            style={{ flex: 1 }}
                        >
                            <option value="true">공개</option>
                            <option value="false">비공개</option>
                        </Select>
                        <Label style={{ marginLeft: '10px' }}>카카오채널 아이디</Label>
                        <Input
                            value={editedHospital.kakaoChannelId}
                            onChange={(e) => handleChange('kakaoChannelId', e.target.value)}
                            placeholder="카카오채널 아이디를 입력하세요"
                            disabled={!isEditing}
                            style={{ flex: 1 }}
                        />
                    </InfoRow>
                    <InfoRow>
                        <Label style={{ marginLeft: '10px' }}>병원 코드</Label>
                        <Input
                            value={editedHospital.code}
                            disabled={true}
                            style={{ flex: 1 }}
                        />
                        <Label style={{ marginLeft: '10px' }}>키오스크 코드</Label>
                        <Input
                            value={editedHospital.clientCode}
                            onChange={(e) => handleChange('clientCode', e.target.value)}
                            placeholder="키오스크 병원 코드를 입력하세요."
                            disabled={!isEditing}
                            style={{ flex: 1 }}
                        />
                        <Label style={{ marginLeft: '10px' }}>Mall 아이디</Label>
                        <Input
                            value={editedHospital.mallSellerId}
                            onChange={(e) => handleChange('mallSellerId', e.target.value)}
                            placeholder="Mall 아이디를 입력하세요."
                            disabled={!isEditing}
                            style={{ flex: 1 }}
                        />
                    </InfoRow>
                    <InfoRow>
                        <Label>우편번호</Label>
                        <Input
                            value={editedHospital.zipCode}
                            onChange={(e) => handleChange('zipCode', e.target.value)}
                            placeholder="우편번호를 입력하세요"
                            disabled={!isEditing}
                        />
                        <Label>주소</Label>
                        <Input
                            value={editedHospital.address}
                            onChange={(e) => handleChange('address', e.target.value)}
                            placeholder="주소를 입력하세요"
                            disabled={!isEditing}
                            style={{ flex: 1 }}
                        />
                        <Label style={{ marginLeft: '10px' }}>상세주소</Label>
                        <Input
                            value={editedHospital.addressDetail}
                            onChange={(e) => handleChange('addressDetail', e.target.value)}
                            placeholder="상세주소를 입력하세요"
                            disabled={!isEditing}
                            style={{ flex: 1 }}
                        />
                    </InfoRow>
                    <InfoRow>
                        <Label>소개</Label>
                        <TextArea
                            value={editedHospital.bio}
                            onChange={(e) => handleChange('bio', e.target.value)}
                            placeholder="병원 소개를 입력하세요"
                            disabled={!isEditing}
                        />
                    </InfoRow>
                    <InfoRow>
                        <Label>진료과목</Label>
                        <Input
                            value={editedHospital.departments?.join(', ')}
                            onChange={(e) => handleChange('departments', e.target.value.split(',').map(item => item.trim()))}
                            placeholder="진료과목을 쉼표로 구분하여 입력하세요"
                            disabled={!isEditing}
                            style={{ flex: 1 }}
                        />
                        <Label style={{ marginLeft: '10px' }}>위도</Label>
                        <Input
                            type="number"
                            value={editedHospital.lat}
                            onChange={(e) => handleChange('lat', parseFloat(e.target.value))}
                            placeholder="위도를 입력하세요"
                            disabled={!isEditing}
                            style={{ flex: 1 }}
                        />
                        <Label style={{ marginLeft: '10px' }}>경도</Label>
                        <Input
                            type="number"
                            value={editedHospital.lng}
                            onChange={(e) => handleChange('lng', parseFloat(e.target.value))}
                            placeholder="경도를 입력하세요"
                            disabled={!isEditing}
                            style={{ flex: 1 }}
                        />
                    </InfoRow>
                    <InfoRow>
                        <Label>운영 시간</Label>
                        <OperatingHoursSection>
                            {(editedHospital.operatingHours || initialOperatingHours).map((hour, index) => (
                                <OperatingHourRow key={index}>
                                    <DayLabel>
                                        {index === 0 ? '월요일' :
                                         index === 1 ? '화요일' :
                                         index === 2 ? '수요일' :
                                         index === 3 ? '목요일' :
                                         index === 4 ? '금요일' :
                                         index === 5 ? '토요일' :
                                         index === 6 ? '일요일' : '공휴일'}
                                    </DayLabel>
                                    <TimeInput
                                        type="time"
                                        value={formatTime(hour.open)}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOperatingHourChange(index, 'open', e.target.value)}
                                        disabled={!isEditing || hour.isHoliday}
                                    />
                                    <TimeInput
                                        type="time"
                                        value={formatTime(hour.close)}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOperatingHourChange(index, 'close', e.target.value)}
                                        disabled={!isEditing || hour.isHoliday}
                                    />
                                    <Label style={{ marginLeft: '10px', width: 'auto' }}>점심시간</Label>
                                    <TimeInput
                                        type="time"
                                        value={hour.breakTime?.split('~')[0] || ''}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            const endTime = hour.breakTime?.split('~')[1] || '14:00';
                                            handleOperatingHourChange(index, 'breakTime', `${e.target.value}~${endTime}`);
                                        }}
                                        disabled={!isEditing || hour.isHoliday || !hour.breakTime}
                                    />
                                    <span>~</span>
                                    <TimeInput
                                        type="time"
                                        value={hour.breakTime?.split('~')[1] || ''}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            const startTime = hour.breakTime?.split('~')[0] || '13:00';
                                            handleOperatingHourChange(index, 'breakTime', `${startTime}~${e.target.value}`);
                                        }}
                                        disabled={!isEditing || hour.isHoliday || !hour.breakTime}
                                    />
                                    <label style={{ marginLeft: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <BreakTimeCheckbox
                                            type="checkbox"
                                            checked={!hour.breakTime}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                if (e.target.checked) {
                                                    handleOperatingHourChange(index, 'breakTime', undefined);
                                                } else {
                                                    handleOperatingHourChange(index, 'breakTime', '13:00~14:00');
                                                }
                                            }}
                                            disabled={!isEditing || hour.isHoliday}
                                        />
                                        점심시간 없음
                                    </label>
                                    <label style={{ marginLeft: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <HolidayCheckbox
                                            type="checkbox"
                                            checked={hour.isHoliday}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOperatingHourChange(index, 'isHoliday', e.target.checked)}
                                            disabled={!isEditing}
                                        />
                                        휴무
                                    </label>
                                </OperatingHourRow>
                            ))}
                        </OperatingHoursSection>
                    </InfoRow>
                    <InfoRow>
                        <Label>서비스</Label>
                        <ServiceSection>
                            <ServiceCheckboxGroup>
                                <ServiceCheckboxItem>
                                    <ServiceCheckbox
                                        type="checkbox"
                                        checked={(editedHospital.services || []).includes('DCK')}
                                        onChange={(e) => handleServiceChange('DCK', e.target.checked)}
                                        disabled={!isEditing}
                                    />
                                    <ServiceLabel>상담 서비스</ServiceLabel>
                                </ServiceCheckboxItem>
                                <ServiceCheckboxItem>
                                    <ServiceCheckbox
                                        type="checkbox"
                                        checked={(editedHospital.services || []).includes('CL')}
                                        onChange={(e) => handleServiceChange('CL', e.target.checked)}
                                        disabled={!isEditing}
                                    />
                                    <ServiceLabel>칼로리랩</ServiceLabel>
                                </ServiceCheckboxItem>
                            </ServiceCheckboxGroup>
                        </ServiceSection>
                    </InfoRow>
                    <InfoRow>
                        <Label>의사 정보</Label>
                        <DoctorSection>
                            {editedHospital.doctors?.map((doctor, index) => (
                                <DoctorCard key={index}>
                                    <DoctorInputGroup>
                                        <DoctorInputRow>
                                            <DoctorImageSection>
                                                <DoctorImagePreview>
                                                    {doctor.image ? (
                                                        <img src={doctor.image} alt={`${doctor.name} 의사 사진`} />
                                                    ) : (
                                                        <span style={{ color: '#999', fontSize: '14px' }}>사진 없음</span>
                                                    )}
                                                </DoctorImagePreview>
                                            </DoctorImageSection>
                                            <DoctorInfoSection>
                                                <Label>이름</Label>
                                                <Input
                                                    value={doctor.name}
                                                    onChange={(e) => handleDoctorChange(index, 'name', e.target.value)}
                                                    placeholder="의사 이름을 입력하세요"
                                                    disabled={!isEditing}
                                                    style={{ flex: 1 }}
                                                />
                                                <Label>진료과</Label>
                                                <Input
                                                    value={doctor.departments?.join(', ') || ''}
                                                    onChange={(e) => handleDoctorChange(index, 'departments', e.target.value.split(',').map(item => item.trim()))}
                                                    placeholder="진료과를 쉼표로 구분하여 입력하세요"
                                                    disabled={!isEditing}
                                                    style={{ flex: 2 }}
                                                />
                                            </DoctorInfoSection>
                                            {isEditing && (
                                                <DoctorActionButtons>
                                                    {doctor.name && (
                                                        <DoctorImageUploadButton onClick={() => {
                                                            setSelectedDoctorIndex(index);
                                                            doctorFileInputRef.current?.click();
                                                        }}>
                                                            {doctor.image ? '사진 변경' : '사진 등록'}
                                                        </DoctorImageUploadButton>
                                                    )}
                                                    <RemoveButton onClick={() => handleRemoveDoctor(index)}>
                                                        삭제
                                                    </RemoveButton>
                                                </DoctorActionButtons>
                                            )}
                                        </DoctorInputRow>
                                    </DoctorInputGroup>
                                </DoctorCard>
                            ))}
                            {isEditing && (
                                <AddDoctorButton onClick={handleAddDoctor}>
                                    + 의사 추가
                                </AddDoctorButton>
                            )}
                        </DoctorSection>
                    </InfoRow>
                    <InfoRow>
                        <Label>병원 사진</Label>
                        <ImageUploadSection>
                            <ImagePreviewContainer>
                                {previewImages.map((image, index) => (
                                    <ImagePreview key={index} onClick={() => handleImagePreview(image.url)}>
                                        <img src={image.url} alt={`병원 사진 ${index + 1}`} />
                                        {isEditing && (
                                            <RemoveImageButton onClick={() => handleDeleteImageClick(index)}>
                                                삭제
                                            </RemoveImageButton>
                                        )}
                                    </ImagePreview>
                                ))}
                            </ImagePreviewContainer>
                            {isEditing && (
                                <ImageUploadButton onClick={() => fileInputRef.current?.click()}>
                                    + 사진 추가
                                </ImageUploadButton>
                            )}
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleImageUpload}
                                accept="image/*"
                                multiple
                                style={{ display: 'none' }}
                            />
                        </ImageUploadSection>
                    </InfoRow>
                    <InfoRow>
                        <Label>상품</Label>
                        <ProductSelectionContainer>
                            <SearchContainer>
                                <SearchInput
                                    type="text"
                                    placeholder="상품명으로 검색"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onFocus={() => setIsProductListVisible(true)}
                                    disabled={!isEditing}
                                />
                                {searchTerm && (
                                    <ClearSearchButton onClick={() => setSearchTerm('')}>
                                        ×
                                    </ClearSearchButton>
                                )}
                                {isProductListVisible && (
                                    <CloseListButton onClick={() => setIsProductListVisible(false)}>
                                        X
                                    </CloseListButton>
                                )}
                            </SearchContainer>
                            {isProductListVisible && (
                                <ProductList>
                                    {filteredProducts.map((product) => (
                                        <ProductItem
                                            key={product._id}
                                            onClick={() => {
                                                handleProductClick(product.externalCode);
                                                setIsProductListVisible(false);
                                            }}
                                            selected={selectedProducts.includes(product.externalCode)}
                                            disabled={!isEditing}
                                        >
                                            {product.providerName} - {product.productName} ({product.retailPrice.toLocaleString()}원)
                                        </ProductItem>
                                    ))}
                                </ProductList>
                            )}
                            <HelperText>상품을 클릭하여 선택/해제할 수 있습니다.</HelperText>
                            <SelectedProductsContainer>
                                {selectedProducts.map((externalCode) => {
                                    const product = getSelectedProductInfo(externalCode);
                                    return product ? (
                                        <ProductTag key={externalCode}>
                                            <ProductTagText>
                                                {product.externalCode} - {product.providerName} - {product.productName} ({product.retailPrice.toLocaleString()}원)
                                            </ProductTagText>
                                            {isEditing && (
                                                <RemoveProductButton onClick={() => handleRemoveProduct(externalCode)}>
                                                    ×
                                                </RemoveProductButton>
                                            )}
                                        </ProductTag>
                                    ) : null;
                                })}
                            </SelectedProductsContainer>
                        </ProductSelectionContainer>
                    </InfoRow>
                </Content>
            </DetailSection>
            {isDeleteModalOpen && (
                <BasicConfirmModal
                    title="이미지 삭제"
                    content="선택한 이미지를 삭제하시겠습니까?"
                    cancelButton={() => setIsDeleteModalOpen(false)}
                    confirmButton={handleRemoveImage}
                    firstButton="취소"
                    secondButton="삭제"
                />
            )}
            {isSaveModalOpen && (
                <BasicConfirmModal
                    title="병원 정보 저장"
                    content="변경된 정보를 저장하시겠습니까?"
                    cancelButton={() => setIsSaveModalOpen(false)}
                    confirmButton={handleSaveConfirm}
                    firstButton="취소"
                    secondButton="저장"
                />
            )}
            {isPreviewModalOpen && (
                <ImagePreviewModal onClick={() => setIsPreviewModalOpen(false)}>
                    <ImagePreviewContent onClick={(e) => e.stopPropagation()}>
                        <img src={selectedPreviewImage} alt="병원 사진 미리보기" />
                    </ImagePreviewContent>
                </ImagePreviewModal>
            )}
            {isDeleteHospitalModalOpen && (
                <BasicConfirmModal
                    title="병원 삭제"
                    content="선택한 병원을 삭제하시겠습니까?"
                    cancelButton={() => setIsDeleteHospitalModalOpen(false)}
                    confirmButton={handleDeleteHospital}
                    firstButton="취소"
                    secondButton="삭제"
                />
            )}
            <input
                type="file"
                ref={doctorFileInputRef}
                onChange={(e) => selectedDoctorIndex !== null && handleDoctorImageUpload(e, selectedDoctorIndex)}
                accept="image/*"
                style={{ display: 'none' }}
            />
        </>
    );
};

export default AdminManageHospitalDetail;

const DetailSection = styled.div`
    width: 100%;
    padding: 24px;
    background-color: white;
    border-radius: 4px;
    border: 1px solid ${BorderColor.color_border_normal};
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
`;

const Title = styled(Body3Medium)`
    font-size: 20px;
    font-weight: 600;
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 8px;
    
    button {
        min-width: 80px;
        padding: 8px 16px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const InfoRow = styled.div`
    display: flex;
    gap: 24px;
`;

const Label = styled(Body3Medium)`
    width: 120px;
    color: #000000;
    margin-left: 10px;
`;

const Input = styled.input<{ disabled?: boolean }>`
    flex: 1;
    padding: 8px;
    border: 1px solid ${props => props.disabled ? BorderColor.color_border_neutral : BorderColor.color_border_normal};
    border-radius: 4px;
    font-size: 14px;
    background-color: ${props => props.disabled ? '#f5f5f5' : 'white'};
    color: #000000;
    cursor: ${props => props.disabled ? 'not-allowed' : 'text'};
`;

const Select = styled.select<{ disabled?: boolean }>`
    flex: 1;
    padding: 8px;
    border: 1px solid ${props => props.disabled ? BorderColor.color_border_neutral : BorderColor.color_border_normal};
    border-radius: 4px;
    font-size: 14px;
    background-color: ${props => props.disabled ? '#f5f5f5' : 'white'};
    color: #000000;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
`;

const TextArea = styled.textarea<{ disabled?: boolean }>`
    flex: 1;
    padding: 8px;
    border: 1px solid ${props => props.disabled ? BorderColor.color_border_neutral : BorderColor.color_border_normal};
    border-radius: 4px;
    font-size: 14px;
    min-height: 100px;
    resize: vertical;
    background-color: ${props => props.disabled ? '#f5f5f5' : 'white'};
    color: #000000;
    cursor: ${props => props.disabled ? 'not-allowed' : 'text'};
`;

const DoctorSection = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const DoctorCard = styled.div`
    border: 1px solid ${BorderColor.color_border_normal};
    border-radius: 8px;
    padding: 10px;
    background-color: white;
`;

const DoctorInputGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const DoctorInputRow = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    position: relative;
    padding-right: 80px;
`;

const DoctorImageSection = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 100px;
`;

const DoctorImagePreview = styled.div`
    width: 100px;
    height: 100px;
    border: 1px solid ${BorderColor.color_border_normal};
    border-radius: 4px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f5f5f5;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const DoctorImageUploadButton = styled.button`
    padding: 8px 16px;
    background-color: #f0f0f0;
    border: 1px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    &:hover {
        background-color: #e0e0e0;
    }
`;

const DoctorInfoSection = styled.div`
    display: flex;
    flex: 1;
    gap: 16px;
    align-items: center;
`;

const ImagePreviewModal = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    cursor: pointer;
`;

const ImagePreviewContent = styled.div`
    max-width: 90%;
    max-height: 90%;
    cursor: default;

    img {
        max-width: 100%;
        max-height: 90vh;
        object-fit: contain;
    }
`;

const ImageUploadSection = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const ImagePreviewContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
`;

const ImagePreview = styled.div`
    position: relative;
    width: 150px;
    height: 150px;
    border: 1px solid ${BorderColor.color_border_normal};
    border-radius: 4px;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const ImageUploadButton = styled.button`
    width: 150px;
    height: 150px;
    border: 1px dashed ${BorderColor.color_border_normal};
    border-radius: 4px;
    background-color: #f5f5f5;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: #666;

    &:hover {
        background-color: #e0e0e0;
    }
`;

const RemoveImageButton = styled.button`
    position: absolute;
    top: 8px;
    right: 8px;
    padding: 4px 8px;
    background-color: rgba(255, 0, 0, 0.8);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;

    &:hover {
        background-color: rgba(255, 0, 0, 1);
    }
`;

const OperatingHoursSection = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const OperatingHourRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const DayLabel = styled.div`
    width: 60px;
    font-size: 14px;
`;

const TimeInput = styled.input`
    width: 150px;
    padding: 4px 8px;
    border: 1px solid ${BorderColor.color_border_normal};
    border-radius: 4px;
    font-size: 14px;
    background-color: white;
    color: #000000;
    cursor: text;
    
    &::-webkit-calendar-picker-indicator {
        cursor: pointer;
    }
    
    &::-webkit-datetime-edit {
        padding: 0;
    }
    
    &::-webkit-datetime-edit-fields-wrapper {
        padding: 0;
    }
    
    &::-webkit-datetime-edit-text {
        padding: 0 2px;
    }
    
    &::-webkit-datetime-edit-hour-field,
    &::-webkit-datetime-edit-minute-field {
        padding: 0 2px;
    }
`;

const HolidayCheckbox = styled.input`
    margin-left: 8px;
    cursor: pointer;
`;

const BreakTimeCheckbox = styled.input`
    margin-left: 8px;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
`;

const DeleteButton = styled(NegativeButton)`
    background-color: #ff4444;
    margin-left: 16px;
    &:hover {
        background-color: #cc0000;
    }
`;

const DoctorActionButtons = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    white-space: nowrap;
`;

const RemoveButton = styled.button`
    padding: 6px 12px;
    background-color: #ff4444;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    margin-left: 8px;
    &:hover {
        background-color: #cc0000;
    }
`;

const AddDoctorButton = styled.button`
    padding: 12px 24px;
    background-color: #f0f0f0;
    border: 1px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
    margin-top: 8px;
    font-size: 14px;
    &:hover {
        background-color: #e0e0e0;
    }
`;

const ServiceSection = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
`;

const ServiceCheckboxGroup = styled.div`
    display: flex;
    gap: 24px;
`;

const ServiceCheckboxItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ServiceCheckbox = styled.input`
    width: 16px;
    height: 16px;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
`;

const ServiceLabel = styled.label`
    font-size: 14px;
    color: #000000;
    cursor: pointer;
`;

const HelperText = styled.span`
    font-size: 12px;
    color: #666;
    margin-left: 8px;
`;

const ProductSelectionContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const SelectedProductsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
    width: 500px;
`;

const ProductTag = styled.div`
    display: flex;
    align-items: center;
    background-color: #f0f0f0;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 4px 8px;
    gap: 4px;
`;

const ProductTagText = styled.span`
    font-size: 14px;
    color: #333;
`;

const RemoveProductButton = styled.button`
    background: none;
    border: none;
    color: #666;
    font-size: 16px;
    cursor: pointer;
    padding: 0 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
        color: #ff4444;
    }
`;

const SearchContainer = styled.div`
    position: relative;
    width: 300px;
`;

const SearchInput = styled.input<{ disabled?: boolean }>`
    width: 100%;
    padding: 8px;
    padding-right: 32px;
    border: 1px solid ${props => props.disabled ? BorderColor.color_border_neutral : BorderColor.color_border_normal};
    border-radius: 4px;
    font-size: 14px;
    background-color: ${props => props.disabled ? '#f5f5f5' : 'white'};
    color: #000000;
    cursor: ${props => props.disabled ? 'not-allowed' : 'text'};

    &::placeholder {
        color: #999;
    }
`;

const ClearSearchButton = styled.button`
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #666;
    font-size: 16px;
    cursor: pointer;
    padding: 0 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
        color: #ff4444;
    }
`;

const CloseListButton = styled.button`
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #666;
    font-size: 16px;
    cursor: pointer;
    padding: 0 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 24px;
    
    &:hover {
        color: #ff4444;
    }
`;

const ProductList = styled.div`
    width: 700px;
    border: 1px solid ${BorderColor.color_border_normal};
    border-radius: 4px;
    overflow-y: auto;
    min-height: 200px;
    max-height: 300px;
    background-color: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ProductItem = styled.div<{ selected?: boolean; disabled?: boolean }>`
    padding: 8px 12px;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    background-color: ${props => props.selected ? '#e6f3ff' : 'white'};
    border-bottom: 1px solid ${BorderColor.color_border_neutral};
    font-size: 14px;
    color: ${props => props.disabled ? '#999' : '#333'};
    
    &:hover {
        background-color: ${props => props.disabled ? 'white' : props.selected ? '#e6f3ff' : '#f5f5f5'};
    }
    
    &:last-child {
        border-bottom: none;
    }
`;